import { GetServerSideProps, GetStaticPropsContext } from 'next';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import dynamic from 'next/dynamic';

import { getCurrentLocale } from 'utils/getCurrentLocale';
import {
  apolloClient,
  defaultQueryResult,
  getPageQuery,
} from 'utils/apolloClient';

import { PRODUCT_DESIGN_QUERY } from '../../graphql/queries';

import { prepareSeoFromPageData } from '../../components/core/SEO/interface';
import { PageQuery } from '../../graphql/model/PageQuery';
import {
  ProductDesignQueryType,
  ProductDesignType,
} from '../../models/pageTypes/services/product-design';
import { Page } from '../../models/pageTypes/page';
import {
  JoinUsWrapper,
  ProjectsWrapper,
  Wrapper,
} from '../../styles/pages/styled/services/product-design.styles';
import { Parallax } from '../../components/sections/ProductDesign/Parallax/Parallax';
import { TitleAs } from '../../models/enums/titleAs';

const DynamicWhatWeHaveBuilt = dynamic(() =>
  import(
    '../../components/sections/ProductDesign/WhatWeHaveBuilt/WhatWeHaveBuilt'
  ).then((mod) => mod.WhatWeHaveBuilt)
);
const DynamicTestimonial = dynamic(
  () => import('../../components/sections/Testimonial')
);
const DynamicLiveCollaboration = dynamic(() =>
  import(
    '../../components/sections/ProductDesign/LiveCollaboration/LiveCollaboration'
  ).then((mod) => mod.LiveCollaboration)
);
const DynamicOurProcess = dynamic(() =>
  import('../../components/sections/OurProcess/OurProcess').then(
    (mod) => mod.OurProcess
  )
);

const DynamicProcessTimeline = dynamic(() =>
  import('../../components/sections/ProcessTimeline/ProcessTimeline').then(
    (mod) => mod.ProcessTimeline
  )
);
const DynamicJoinUs = dynamic(() => import('../../components/sections/JoinUs'));
const DynamicProjects = dynamic(
  () => import('../../components/sections/Projects')
);

const ProductDesign = ({ data: { page } }: Page<ProductDesignType>) => {
  const sections = page.sections.map((section) => ({
    title: RichText.asText(section.title),
    subtitle: RichText.asText(section.subtitle),
    description: RichText.asText(section.description),
    subDescription: RichText.asText(section.sub_description),
    subtitleInfo: RichText.asText(section.subtitle_info),
    image: section.image || {},
  }));

  return (
    <Wrapper className={'product-design'}>
      <Parallax
        page={page}
        sections={sections}
        name={'product-design'}
        backButtonColor={'white'}
      />

      <DynamicWhatWeHaveBuilt
        id={'our-work'}
        title={page.what_we_have_built_title}
        dribbble_text={page.dribbble_text}
        behance_text={page.behance_text}
        dribbble={page.dribbble}
        behance={page.behance}
        want_to_see_more={page.want_to_see_more_text}
        images={{
          desktop: page.what_we_have_built_image_desktop,
          tablet: page.what_we_have_built_image_tablet,
          mobile: page.what_we_have_built_image_mobile,
        }}
        mobileImages={page.what_we_have_built_mobile}
        titleAs={'h3'}
      />
      <DynamicOurProcess
        id={'design-process'}
        title={RichText.asText(page.design_process_title)}
        subtitle={page.design_process_subtitle}
        items={page.design_process_diagram}
        titleAs={TitleAs.h4}
      >
        <DynamicProcessTimeline image={page.process_timeline_image} />
      </DynamicOurProcess>
      <DynamicLiveCollaboration
        id={'collaboration'}
        title={page.live_collaboration_title}
        text1={page.live_collaboration_text1}
        text2={page.live_collaboration_text2}
      />
      <DynamicTestimonial
        additionalClassName={'product-design__testimonial'}
        title={RichText.asText(page.what_is_like_to_title)}
        quote={page.what_is_like_to_quote}
        caseName={RichText.asText(page.what_is_like_to_person)}
        casePosition={RichText.asText(page.what_is_like_to_person_position)}
        caseStudy
        titleAs={TitleAs.h4}
      />
      <ProjectsWrapper>
        <DynamicProjects
          items={page.projects}
          title={RichText.asText(page.projects_title)}
          description={page.projects_description}
          titleAs={TitleAs.h5}
        />
      </ProjectsWrapper>
      <JoinUsWrapper>
        <DynamicJoinUs
          id={'join-us'}
          title={RichText.asText(page.join_us.title)}
          description={RichText.asText(page.join_us.description)}
          actionLabel={RichText.asText(page.join_us.action_label)}
          actionLink={page.join_us.action_link!.url || ''}
          personImage={page.join_us.person_image}
          personName={RichText.asText(page.join_us.person_name)}
          personPosition={'Product Designer'}
          titleAs={TitleAs.h6}
        />
      </JoinUsWrapper>
    </Wrapper>
  );
};

export const getStaticProps: GetServerSideProps = async (
  context: GetStaticPropsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  const {
    data: { allPage_product_designs, ...defaultData },
  } = await apolloClient.query<PageQuery<ProductDesignQueryType>>(
    getPageQuery(
      {
        pageLang: currentLanguage,
      },
      'product_design_query',
      PRODUCT_DESIGN_QUERY
    )
  );

  return {
    props: {
      data: {
        ...defaultQueryResult(defaultData),
        seo: prepareSeoFromPageData(allPage_product_designs.edges[0].node),
        page: {
          ...allPage_product_designs.edges[0].node,
        },
      },
    },
  };
};

export default ProductDesign;
